<script setup lang="ts">
  import { useModal } from '@/composition/use-modal';
  import { useAuth } from '@/composition/use-auth';
  import { OptionType } from '@/enums/OptionType';
  import { PermissionType } from '@/enums/PermissionType';
  import { AlertType } from '@/interfaces/IAlert';
  import { IOption } from '@/interfaces/IOption';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import {
    ref,
    computed,
    getCurrentInstance,
    onMounted,
    watch,
    provide,
  } from 'vue';
  import V2ModalOptionBasic from './V2ModalOptionBasic.vue';
  import ModalOptionStock from './ModalOptionStock.vue';
  import V2ModalOptionTranslations from './V2ModalOptionTranslations.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import RestaurantTierChip from '@/components/partials/RestaurantTierChip.vue';
  import { useUi } from '@/composition/use-ui';
  import OptionsService, {
    ICreateOptionDto,
    IUpdateOptionDto,
    IUpdateOptionItemDto,
  } from '@/api/services/options.service';

  const props = withDefaults(
    defineProps<{
      /** Persisted option to edit (a fresh copy is fetched on open). */
      optionToEdit?: IOption | null;
      /** Draft mode: no API calls, emits `draftSaved` with the DTO. */
      deferred?: boolean;
      /** Re-edit an unsaved draft (used together with `deferred`). */
      draftData?: ICreateOptionDto | null;
      /** A new group whose draft only pre-sets it (a template or a copy). */
      creating?: boolean;
      /** When duplicating, hand the copy back as a draft instead of creating it (item editor context). */
      duplicateAsDraft?: boolean;
      /** Menu the option belongs to when created from the options page. */
      menuId?: number | null;
      /** Shows the delete action; only the options page enables it. */
      deletable?: boolean;
    }>(),
    {
      optionToEdit: null,
      deferred: false,
      draftData: null,
      creating: false,
      duplicateAsDraft: false,
      menuId: null,
      deletable: false,
    }
  );

  const modals = useModal();
  const restaurantStore = useRestaurantStore();
  const ui = useUi();
  const auth = useAuth();
  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const isDeferred = ref(!!props.deferred);
  /** Full persisted option (with usages) — null for new options and drafts. */
  const persistedOption = ref<IOption | null>(null);
  const loading = ref(false);

  const baseOptionData: ICreateOptionDto = {
    type: OptionType.Choose,
    title: '',
    description: null,
    main: false,
    mandatory: false,
    multiple: false,
    min: null,
    max: null,
    optionItems: [],
  };

  const optionData = ref<ICreateOptionDto | IUpdateOptionDto>({
    ...baseOptionData,
    optionItems: [],
  });

  const persistedOptionRef = computed(() => persistedOption.value);
  provide('optionToEdit', persistedOptionRef);
  provide('optionData', optionData);

  /** Rows need stable ids for list keys; unsaved rows get local negative ones. */
  function assignLocalIds(data: ICreateOptionDto) {
    for (const oI of data.optionItems as IUpdateOptionItemDto[]) {
      if (!oI.id) oI.id = -Math.round(Math.random() * 1000000000);
    }
  }

  onMounted(async () => {
    if (props.optionToEdit?.id) {
      await loadPersistedOption(props.optionToEdit.id);
    } else if (props.draftData) {
      const draft = {
        ...baseOptionData,
        ...JSON.parse(JSON.stringify(props.draftData)),
      };
      assignLocalIds(draft);
      optionData.value = draft;
    }
  });

  async function loadPersistedOption(id: number) {
    try {
      loading.value = true;
      const option = await OptionsService.getById(id);
      persistedOption.value = option;
      optionData.value = {
        ...baseOptionData,
        ...JSON.parse(JSON.stringify(option)),
      };
      loading.value = false;
    } catch (e) {
      ui.serverError(e, i18nT('optionEditor.loadError'));
      // The modal unmounts here; don't touch reactive state afterwards
      modals.hide('option');
    }
  }

  watch(
    () => optionData.value.multiple,
    (multiple) => {
      if (multiple) {
        if (optionData.value.min === null) optionData.value.min = 0;
      } else {
        optionData.value.min = null;
        optionData.value.max = null;
      }
    },
    {
      immediate: true,
    }
  );

  watch(
    [() => optionData.value.min, () => optionData.value.max],
    ([min, max]) => {
      if (min === 0) {
        optionData.value.min = null;
      }

      if (max === 0) {
        optionData.value.max = null;
      }
    },
    {
      immediate: true,
    }
  );

  //#region Usages (blast radius)
  const usedByItems = computed<string[]>(() => {
    const itemOptions = persistedOption.value?.itemOptions ?? [];
    return itemOptions
      .map((io) => io.item?.name)
      .filter((name): name is string => !!name);
  });

  const showUsageBanner = computed(
    () => !isDeferred.value && usedByItems.value.length >= 2
  );
  //#endregion

  /** Narrows working data (which may carry entity fields) down to the create/update DTO shape. */
  function sanitizeDto(
    data: ICreateOptionDto | IUpdateOptionDto
  ): ICreateOptionDto {
    return {
      type: data.type,
      title: data.title,
      description: data.description ?? null,
      main: data.main,
      mandatory: data.mandatory,
      multiple: data.multiple,
      min: data.min,
      max: data.max,
      optionItems: (data.optionItems as IUpdateOptionItemDto[]).map(
        (oI): IUpdateOptionItemDto => ({
          id: oI.id,
          title: oI.title,
          description: oI.description ?? null,
          picture: oI.picture ?? null,
          cost: oI.cost,
          discountedCost: oI.discountedCost,
          active: oI.active,
          index: oI.index,
          deletedAt: oI.deletedAt,
        })
      ),
    };
  }

  //#region Duplicate
  const duplicating = ref(false);

  function buildDuplicateDto(): ICreateOptionDto {
    const clone: ICreateOptionDto = JSON.parse(
      JSON.stringify(sanitizeDto(optionData.value))
    );
    clone.title = `${clone.title} ${i18nT('optionEditor.copySuffix')}`;
    clone.optionItems = (clone.optionItems as IUpdateOptionItemDto[])
      .filter((oI) => !oI.deletedAt)
      .map((oI) => {
        const { id, deletedAt, ...rest } = oI;
        return rest;
      });
    return clone;
  }

  async function duplicateOption() {
    const confirmed = await modals.confirm({
      title: i18nT('optionEditor.duplicateConfirmTitle'),
      message: i18nT('optionEditor.duplicateConfirmMessage'),
    });
    if (!confirmed) return;

    const clone = buildDuplicateDto();

    if (props.duplicateAsDraft) {
      // Item editor context: continue as an unsaved draft; the caller will
      // replace its row when `draftSaved` fires.
      persistedOption.value = null;
      isDeferred.value = true;
      assignLocalIds(clone);
      optionData.value = clone;
      ui.showToast(AlertType.Info, i18nT('optionEditor.duplicatedDraft'));
      return;
    }

    try {
      duplicating.value = true;
      const sourceMenuId = persistedOption.value?.menuId ?? props.menuId;
      const created = await OptionsService.create({
        ...clone,
        restaurantId: restaurantStore.restaurant.id,
        itemId: null,
        menuId: sourceMenuId ?? null,
      });
      // Let listeners (options page) refresh, then keep editing the copy.
      proxy.$parent?.$emit('saved', created);
      duplicating.value = false;
      // Handles its own errors and may unmount the modal on failure, so no
      // reactive state is mutated after this point. The API always returns
      // the id of the newly created option.
      await loadPersistedOption(created.id!);
      ui.showToast(AlertType.Success, i18nT('optionEditor.duplicated'));
    } catch (e) {
      ui.serverError(e, i18nT('optionEditor.saveError'));
      duplicating.value = false;
    }
  }
  //#endregion

  //#region Delete
  const deleting = ref(false);

  const canDelete = computed(
    () => props.deletable && !isDeferred.value && !!persistedOption.value?.id
  );

  async function deleteOption() {
    if (deleting.value || !persistedOption.value?.id) return;

    const option = persistedOption.value;
    const items = usedByItems.value;

    const confirmed = await modals.confirm({
      title: i18nT('optionEditor.deleteConfirmTitle'),
      message: items.length
        ? i18nT('optionEditor.deleteConfirmMessageUsed', {
            title: option.title,
            items: items.join(', '),
          })
        : i18nT('optionEditor.deleteConfirmMessage', { title: option.title }),
      acceptText: i18nT('delete'),
      cancelText: i18nT('cancel'),
      acceptColor: 'error',
    });
    if (!confirmed) return;

    try {
      deleting.value = true;
      await OptionsService.delete(option.id!);
      ui.showToast(AlertType.Success, i18nT('optionEditor.deletedToast'));
      proxy.$parent?.$emit('deleted', option.id);
      // The modal unmounts here; don't touch reactive state afterwards
      modals.hide('option');
    } catch (e) {
      ui.serverError(e, i18nT('optionEditor.deleteError'));
      deleting.value = false;
    }
  }
  //#endregion

  function cancel() {
    modals.hide('option');
  }

  //#region Validation
  function validationError(): string | null {
    const data = optionData.value;

    if (!data.title?.trim()) return i18nT('optionEditor.errorTitleRequired');

    const aliveItems = (data.optionItems as IUpdateOptionItemDto[]).filter(
      (oI) => !oI.deletedAt
    );
    if (aliveItems.length === 0) return i18nT('optionEditor.errorNoChoices');
    if (!aliveItems.some((oI) => oI.active))
      return i18nT('optionEditor.errorNoActiveChoices');
    if (aliveItems.some((oI) => !oI.title?.trim()))
      return i18nT('optionEditor.errorChoiceTitleRequired');

    for (const oI of aliveItems) {
      if (oI.discountedCost === null || oI.discountedCost === undefined)
        continue;
      if (!oI.cost)
        return i18nT('optionEditor.errorDiscountNeedsCost', {
          title: oI.title,
        });
      if (oI.discountedCost >= oI.cost)
        return i18nT('optionEditor.errorDiscountTooHigh', { title: oI.title });
    }

    if (data.multiple) {
      if (data.min !== null && data.min > aliveItems.length)
        return i18nT('optionEditor.errorMinTooHigh');
      if (data.max !== null && data.max <= 0)
        return i18nT('optionEditor.errorMaxZero');
      if (data.min !== null && data.max !== null && data.min > data.max)
        return i18nT('optionEditor.errorMinOverMax');
    }

    return null;
  }
  //#endregion

  //#region Save
  const stockTabRef = ref<InstanceType<typeof ModalOptionStock> | null>(null);
  const translationsTabRef = ref<InstanceType<
    typeof V2ModalOptionTranslations
  > | null>(null);

  const saving = ref(false);
  async function save() {
    if (saving.value) return;

    // Inputs may leave strings (or '' when cleared) behind; normalize before
    // validating so the API always receives clean numbers.
    function toNumberOrNull(
      value: number | string | null | undefined
    ): number | null {
      if (value === null || value === undefined || value === '') return null;
      const num = Number(value);
      return isNaN(num) ? null : num;
    }

    function ensureNumeric() {
      const data = optionData.value;
      data.min = toNumberOrNull(data.min);
      data.max = toNumberOrNull(data.max);
      for (const item of data.optionItems) {
        item.cost = toNumberOrNull(item.cost) ?? 0;
        item.discountedCost = toNumberOrNull(item.discountedCost);
      }
    }

    ensureNumeric();
    if (optionData.value.type === OptionType.Add) {
      optionData.value.multiple = true;
      optionData.value.mandatory = false;
    }

    const error = validationError();
    if (error) {
      ui.showToast(AlertType.Error, error);
      return;
    }

    if (isDeferred.value) {
      const draft: ICreateOptionDto = JSON.parse(
        JSON.stringify(sanitizeDto(optionData.value))
      );
      proxy.$parent?.$emit('draftSaved', draft);
      modals.hide('option');
      return;
    }

    try {
      saving.value = true;
      let savedOption: IOption;
      if (!persistedOption.value) {
        savedOption = await OptionsService.create({
          ...sanitizeDto(optionData.value),
          restaurantId: restaurantStore.restaurant.id,
          itemId: null,
          menuId: props.menuId ?? null,
        });
        ui.showToast(AlertType.Success, i18nT('optionEditor.createdToast'));
      } else {
        const dataToUpdate = {
          ...sanitizeDto(optionData.value),
          id: persistedOption.value.id,
        } as IUpdateOptionDto;
        // If id is negative (unsaved rows), set null
        dataToUpdate.optionItems = dataToUpdate.optionItems.map((item) => ({
          ...item,
          id: item.id && item.id > 0 ? item.id : null,
        }));
        savedOption = await OptionsService.update(dataToUpdate);
        ui.showToast(AlertType.Success, i18nT('optionEditor.savedToast'));
      }

      // Lazily-saved tabs: each mounted tab persists its own data.
      // Distinct refs on purpose — Vue 2 string refs outside v-for don't aggregate.
      const lazyTabs = [stockTabRef.value, translationsTabRef.value].filter(
        (tab) => !!tab
      );
      await Promise.all(lazyTabs.map((tab) => tab.save(savedOption)));

      proxy.$parent?.$emit('saved', savedOption);
      // The modal unmounts here; don't touch reactive state afterwards
      modals.hide('option');
    } catch (error) {
      ui.serverError(error, i18nT('optionEditor.saveError'));
      saving.value = false;
    }
  }
  //#endregion

  //#region Tabs
  const TABS = [
    { key: 'basic', labelKey: 'optionEditor.tabBasic' },
    { key: 'stock', labelKey: 'optionEditor.tabStock' },
    { key: 'translations', labelKey: 'optionEditor.tabTranslations' },
  ] as const;

  /** GET /stock/recipes requires CatalogManage; hide the tab otherwise. */
  const canManageStock = computed(() =>
    auth.hasPermission(PermissionType.CatalogManage)
  );

  const visibleTabs = computed(() =>
    TABS.filter((tab) => tab.key !== 'stock' || canManageStock.value)
  );

  const selectedTabKey = ref<typeof TABS[number]['key']>('basic');
  // Lazy mount + keep alive: a tab renders after its first visit and stays mounted.
  const openedTabKeys = ref<string[]>(['basic']);

  function selectTab(key: typeof TABS[number]['key']) {
    selectedTabKey.value = key;
    if (!openedTabKeys.value.includes(key)) openedTabKeys.value.push(key);
  }

  const headerTitle = computed(() =>
    !props.creating &&
    (props.optionToEdit || persistedOption.value || props.draftData)
      ? i18nT('optionEditor.titleEdit')
      : i18nT('optionEditor.titleNew')
  );
</script>

<template>
  <div class="modal-option">
    <div class="modal-header">
      <span class="modal-title">{{ headerTitle }}</span>
    </div>

    <div class="modal-content">
      <!-- Loading persisted option -->
      <template v-if="loading">
        <v-skeleton-loader type="article" />
      </template>

      <!-- Editor -->
      <template v-else>
        <div v-if="showUsageBanner" class="usage-banner">
          <PosUiIcon name="alert-outline" class="usage-banner-icon" />
          <div class="usage-banner-body">
            <span class="usage-banner-text">
              {{
                $t('optionEditor.usageBanner', {
                  count: usedByItems.length,
                  items: usedByItems.join(', '),
                })
              }}
            </span>
            <PosUiButton
              color="neutral"
              variant="outline"
              size="sm"
              icon="content-copy"
              :loading="duplicating"
              :disabled="deleting"
              class="usage-banner-action"
              @click="duplicateOption"
            >
              {{ $t('optionEditor.duplicate') }}
            </PosUiButton>
          </div>
        </div>

        <!-- Deferred drafts: only the basic tab until the product is saved -->
        <template v-if="isDeferred">
          <span class="deferred-hint">
            {{ $t('optionEditor.deferredHint') }}
          </span>
          <V2ModalOptionBasic />
        </template>

        <template v-else>
          <div class="tab-bar">
            <button
              v-for="tab in visibleTabs"
              :key="tab.key"
              class="tab-button"
              :class="{ active: selectedTabKey === tab.key }"
              @click="selectTab(tab.key)"
            >
              <span>{{ $t(tab.labelKey) }}</span>
              <RestaurantTierChip
                v-if="tab.key === 'translations'"
                :tier="1"
                class="tab-chip"
              />
            </button>
          </div>

          <div class="tab-panes">
            <div v-show="selectedTabKey === 'basic'" class="tab-pane">
              <V2ModalOptionBasic />
            </div>
            <!-- Keyed by the persisted option: duplicating swaps the edited
                 option (new ids), so these per-id tabs must remount and
                 reload instead of rendering stale state. -->
            <div
              v-if="openedTabKeys.includes('stock')"
              v-show="selectedTabKey === 'stock'"
              class="tab-pane"
            >
              <ModalOptionStock
                :key="`stock-${persistedOption ? persistedOption.id : 'new'}`"
                ref="stockTabRef"
              />
            </div>
            <div
              v-if="openedTabKeys.includes('translations')"
              v-show="selectedTabKey === 'translations'"
              class="tab-pane"
            >
              <V2ModalOptionTranslations
                :key="`translations-${
                  persistedOption ? persistedOption.id : 'new'
                }`"
                ref="translationsTabRef"
              />
            </div>
          </div>
        </template>
      </template>
    </div>

    <div class="modal-actions">
      <PosUiButton
        v-if="canDelete"
        class="delete-button"
        color="error"
        variant="soft"
        icon="delete-outline"
        :title="$t('optionEditor.delete')"
        :aria-label="$t('optionEditor.delete')"
        :loading="deleting"
        :disabled="saving || duplicating || loading"
        @click="deleteOption"
      >
        {{ $t('optionEditor.delete') }}
      </PosUiButton>
      <PosUiButton
        color="neutral"
        variant="ghost"
        :disabled="saving || deleting"
        @click="cancel"
      >
        {{ $t('cancel') }}
      </PosUiButton>
      <PosUiButton
        color="primary"
        :loading="saving"
        :disabled="loading || deleting"
        @click="save"
      >
        {{ $t('save') }}
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  // Root lives inside .vm--modal.dialog--height-limit (a max-height flex
  // parent): header/actions stay pinned while the body scrolls.
  // Spacing uses margins, not flex gap: old POS hardware (Chrome < 84).
  .modal-option {
    @apply tw-flex tw-max-h-full tw-min-h-0 tw-w-full tw-flex-col tw-bg-surface;

    .modal-header {
      @apply tw-flex-none tw-border-b tw-border-neutral-200 tw-px-5 tw-py-4;

      .modal-title {
        @apply tw-text-lg tw-font-semibold tw-text-neutral-900;
      }
    }

    .modal-content {
      @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-space-y-4 tw-overflow-y-auto tw-px-5 tw-py-4;

      .usage-banner {
        @apply tw-flex tw-items-start tw-rounded-xl tw-border tw-border-amber-300 tw-bg-amber-50 tw-p-3;

        .usage-banner-icon {
          @apply tw-mt-0.5 tw-text-xl tw-text-amber-700;
        }

        .usage-banner-body {
          @apply tw-ml-3 tw-flex tw-flex-1 tw-flex-col tw-items-start;

          .usage-banner-text {
            @apply tw-text-sm tw-text-amber-900;
          }

          .usage-banner-action {
            @apply tw-mt-2;
          }
        }
      }

      .deferred-hint {
        @apply tw-rounded-lg tw-bg-neutral-100 tw-px-3 tw-py-2 tw-text-sm tw-text-neutral-500;
      }

      .tab-bar {
        @apply tw-flex tw-border-b tw-border-neutral-200;

        .tab-button {
          @apply tw-relative tw-flex tw-min-h-11 tw-items-center tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-neutral-500 tw-transition-colors;

          &:hover {
            @apply tw-text-neutral-900;
          }

          &:focus-visible {
            @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
          }

          &.active {
            @apply tw-text-neutral-900;

            &::after {
              content: '';
              @apply tw-absolute tw-inset-x-1 tw-bottom-0 tw-h-0.5 tw-rounded-full tw-bg-orange-600;
            }
          }

          & + .tab-button {
            @apply tw-ml-1;
          }

          .tab-chip {
            @apply tw-pointer-events-none tw-ml-1.5;
          }
        }
      }

      .tab-panes {
        .tab-pane {
          @apply tw-pt-1;
        }
      }
    }

    .modal-actions {
      @apply tw-flex tw-flex-none tw-items-center tw-justify-end tw-space-x-2 tw-border-t tw-border-neutral-200 tw-px-5 tw-py-3;

      // Kept in the right-hand group (not mr-auto): the support bubble floats
      // bottom-left and would cover a left-aligned action on tablets/phones.
      .delete-button {
        @apply tw-mr-2;

        // Compact widths: icon only, keep the hit target. The label span is
        // inline-flex in PosUiButton; restore that, not plain inline.
        :deep(.pos-ui-button-label) {
          @apply tw-hidden sm:tw-inline-flex;
        }
      }
    }
  }
</style>
